import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import GuideCard from '@/components/feature/GuideCard';
import { guides } from '@/mocks/guides';
import { categories, riskLevels } from '@/mocks/categories';
import { useSeo, buildCanonical } from '@/hooks/useSeo';

export default function GuidesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'all';
  const query = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(query);
  const [activeRisk, setActiveRisk] = useState('all');

  const filtered = useMemo(() => {
    return guides.filter((guide) => {
      const matchCategory = activeCategory === 'all' || guide.category === activeCategory;
      const matchRisk = activeRisk === 'all' || guide.risk === activeRisk;
      const text = `${guide.title}${guide.summary}${guide.tags.join('')}`.toLowerCase();
      const matchKeyword = !query || text.includes(query.toLowerCase());
      return matchCategory && matchRisk && matchKeyword;
    });
  }, [activeCategory, activeRisk, query]);

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === 'all') params.delete('category');
      else params.set('category', next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete('q');
      else params.set('q', next.q);
    }
    setSearchParams(params, { replace: true });
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  const activeCategoryName =
    activeCategory === 'all'
      ? '全部清单'
      : categories.find((c) => c.id === activeCategory)?.name || '全部清单';

  useSeo({
    title: '避雷清单列表｜看料避雷社 消费避雷指南全集',
    description:
      '浏览看料避雷社全部避雷清单，按网购电商、美妆护肤、数码家电、食饮健康、旅宿出行、教育培训、金融理财、家装维修等场景筛选，也可按风险等级与关键词查找你关心的坑点。',
    keywords: '避雷清单,消费避雷,看料黑料避雷,风险等级,避雷指南',
    path: '/guides',
    image: 'https://readdy.ai/api/search-image?query=Stack%20of%20editorial%20warning%20cards%20on%20a%20warm%20cream%20desk%2C%20amber%20and%20brown%20tones%2C%20minimal%20clean%20composition&width=1200&height=630&seq=og-kanliao-guides&orientation=landscape',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '避雷清单列表',
        url: buildCanonical('/guides'),
        inLanguage: 'zh-CN',
        description: '按消费场景与风险等级浏览全部避雷清单。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: buildCanonical('/') },
          { '@type': 'ListItem', position: 2, name: '避雷清单', item: buildCanonical('/guides') },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '避雷清单',
        itemListElement: guides.map((guide, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: guide.title,
          url: buildCanonical(`/guides/${guide.slug}`),
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumbs items={[{ name: '首页', to: '/' }, { name: '避雷清单' }]} />
      <main className="flex-1">
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100/60 border-b border-background-200">
          <span className="inline-flex items-center gap-2 text-xs font-label tracking-widest uppercase text-accent-600 mb-3">
            <span className="w-6 h-px bg-accent-400"></span>
            清单库
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
            全部避雷清单
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed font-body max-w-3xl">
            按消费场景、风险等级和关键词查找你关心的坑点。每份清单都包含常见套路、危险信号和可以直接执行的检查项，
            帮你在下单、签约、出行之前先排一遍雷。
          </p>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10 border-b border-background-200 sticky-none">
          <div className="flex flex-col gap-5">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="输入关键词，如：定金、代购、延保、照骗"
                  className="w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 font-label"
                  aria-label="搜索避雷清单"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-foreground-900 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-2-line"></i>
                搜索
              </button>
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    updateParams({ q: '' });
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-label text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                  清除
                </button>
              )}
            </form>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateParams({ category: 'all' })}
                className={`rounded-full px-3.5 py-1.5 text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${activeCategory === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 border border-background-300 hover:border-primary-300'}`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => updateParams({ category: cat.id })}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${activeCategory === cat.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 border border-background-300 hover:border-primary-300'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-label text-foreground-500 flex items-center gap-1">
                <i className="ri-filter-3-line"></i>
                风险等级
              </span>
              <button
                type="button"
                onClick={() => setActiveRisk('all')}
                className={`rounded-md px-3 py-1 text-xs font-label transition-colors cursor-pointer ${activeRisk === 'all' ? 'bg-secondary-500 text-background-50' : 'bg-secondary-50 text-secondary-800 border border-secondary-200'}`}
              >
                全部
              </button>
              {riskLevels.map((level) => (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => setActiveRisk(level.id)}
                  title={level.desc}
                  className={`rounded-md px-3 py-1 text-xs font-label transition-colors cursor-pointer ${activeRisk === level.id ? 'bg-secondary-500 text-background-50' : 'bg-secondary-50 text-secondary-800 border border-secondary-200'}`}
                >
                  {level.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {activeCategoryName}
              {query && <span className="text-foreground-500 font-body text-sm"> · 关键词「{query}」</span>}
            </h2>
            <span className="text-sm font-label text-foreground-500">共 {filtered.length} 份</span>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((guide) => (
                <GuideCard key={guide.slug} guide={guide} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 px-6 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center text-2xl">
                <i className="ri-search-eye-line"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的清单</h3>
              <p className="mt-2 text-sm text-foreground-600 font-body">
                换一个关键词，或把分类和风险等级都切回「全部」再试一次。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setActiveRisk('all');
                  setSearchParams({}, { replace: true });
                }}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}